import { MousePointerClick, Target, Layers, Zap, BarChart3, Palette, Layout, TestTube, LineChart, Code2 } from "lucide-react";
import SubServicePageLayout from "@/components/SubServicePageLayout";
import { usePageContent } from "@/hooks/usePageContent";
import { resolveIcon } from "@/lib/iconResolver";

const fallback = {
  seoTitle: "High-Converting Landing Pages That Drive Results",
  seoDescription: "Landing pages engineered for conversion. We combine strategy, design, and optimization to maximize the ROI of your traffic.",
  canonicalUrl: "https://xperfomax.com/landing-pages",
  category: "services",
  categoryLabel: "Services / Landing Pages",
  headline: "Landing Pages Engineered for Conversion",
  subheadline: "Beautiful pages are easy. Pages that convert are hard. We build landing pages that do both, turning your traffic into revenue.",
  ctaText: "Get a landing page audit",
  problemTitle: "Why Most Landing Pages Fail",
  problems: [
    "Pages are designed for aesthetics, not conversion outcomes.",
    "Generic messaging fails to resonate with target audiences.",
    "Slow load times erode performance before engagement begins.",
    "No testing infrastructure means no compounding improvement.",
  ],
  systemTitle: "Our Landing Page System",
  systemDescription: "Conversion strategy, performance design, and continuous optimization.",
  services: [
    { name: "Conversion-Focused Design", icon: "MousePointerClick", components: ["Above-the-fold optimization", "Visual hierarchy design", "Trust signal placement"], outcome: "Landing pages engineered to convert, not just impress." },
    { name: "Message-Market Match", icon: "Target", components: ["Audience research & segmentation", "Value proposition clarity", "Ad-to-page consistency"], outcome: "Messaging that resonates with your specific audience." },
    { name: "Page Architecture", icon: "Layers", components: ["Content structure strategy", "Scroll depth optimization", "Mobile-first layouts"], outcome: "Information architecture that guides users to action." },
    { name: "Speed Optimization", icon: "Zap", components: ["Core Web Vitals optimization", "Image & asset compression", "Lazy loading implementation"], outcome: "Sub-3 second load times that reduce bounce rates." },
    { name: "A/B Testing Integration", icon: "BarChart3", components: ["Testing framework setup", "Variant development", "Statistical analysis"], outcome: "Continuous improvement through data-driven testing." },
    { name: "Brand-Aligned Creative", icon: "Palette", components: ["Visual identity consistency", "Custom illustration & graphics", "Photography direction"], outcome: "Pages that convert while strengthening brand equity." },
  ],
  processTitle: "From strategy to conversion.",
  processSteps: [
    { number: "01", title: "Discovery & Strategy", description: "Understand your audience, offer, and conversion goals to define the page strategy." },
    { number: "02", title: "Wireframing", description: "Create content structure and layout that prioritizes conversion over aesthetics." },
    { number: "03", title: "Design & Copy", description: "High-fidelity design with conversion-focused copy that speaks to your audience." },
    { number: "04", title: "Development", description: "Clean, performant code with tracking, forms, and integrations built in." },
    { number: "05", title: "Launch & Optimize", description: "Deploy with monitoring and begin structured testing to improve performance." },
  ],
  technologyCategories: [
    { title: "Design", icon: "Layout", items: ["Figma", "Adobe XD", "Sketch"] },
    { title: "Development", icon: "Code2", items: ["React", "Next.js", "Webflow", "Unbounce"] },
    { title: "Testing", icon: "TestTube", items: ["VWO", "Optimizely", "Google Optimize"] },
    { title: "Analytics", icon: "LineChart", items: ["GA4", "Hotjar", "Microsoft Clarity"] },
  ],
  techTitle: "Landing Page Tools",
  techDescription: "Best-in-class tools for design, development, and optimization.",
  qualification: {
    forYou: [
      "You're running paid campaigns and need pages that convert",
      "Your current landing pages underperform industry benchmarks",
      "You want data-driven design, not just beautiful pages",
      "You're ready to invest in proper testing infrastructure",
    ],
    notForYou: [
      "You need a full website, not campaign-specific pages",
      "You're not running traffic to the pages",
      "You want to design by committee without data",
      "You expect a single page to solve all problems",
    ],
  },
  faqs: [
    { question: "How long does it take to build a landing page?", answer: "A single landing page typically takes 2-3 weeks from strategy to launch. Complex pages or multi-variant builds may take 4-6 weeks." },
    { question: "Do you write the copy or do we need to provide it?", answer: "We handle everything including copywriting. Our conversion copywriters create messaging based on audience research and your value proposition." },
    { question: "What platforms do you build on?", answer: "We're platform-agnostic. We can build on your existing CMS, use dedicated landing page tools like Unbounce, or develop custom React/Next.js pages." },
    { question: "How do you measure landing page success?", answer: "We track conversion rate, cost per conversion, and revenue generated. We also monitor engagement metrics like scroll depth and time on page." },
    { question: "Do you offer ongoing optimization?", answer: "Yes. We offer ongoing optimization packages that include A/B testing, performance monitoring, and iterative improvements based on data." },
  ],
  finalCtaTitle: "Stop wasting traffic.",
  finalCtaDescription: "Your ads are working. Your landing pages should too. Let's build pages that convert.",
  finalCtaButtonText: "Get a landing page audit",
};

const LandingPages = () => {
  const { content } = usePageContent("landing-pages", fallback);

  const services = content.services.map((s: any) => ({
    ...s,
    icon: resolveIcon(s.icon),
  }));

  const technologyCategories = content.technologyCategories.map((t: any) => ({
    ...t,
    icon: resolveIcon(t.icon),
  }));

  return (
    <SubServicePageLayout
      seoTitle={content.seoTitle}
      seoDescription={content.seoDescription}
      canonicalUrl={content.canonicalUrl}
      category={content.category}
      categoryLabel={content.categoryLabel}
      headline={content.headline}
      subheadline={content.subheadline}
      ctaText={content.ctaText}
      problemTitle={content.problemTitle}
      problems={content.problems}
      systemTitle={content.systemTitle}
      systemDescription={content.systemDescription}
      services={services}
      processTitle={content.processTitle}
      processSteps={content.processSteps}
      technologyCategories={technologyCategories}
      techTitle={content.techTitle}
      techDescription={content.techDescription}
      qualification={content.qualification}
      faqs={content.faqs}
      finalCtaTitle={content.finalCtaTitle}
      finalCtaDescription={content.finalCtaDescription}
      finalCtaButtonText={content.finalCtaButtonText}
    />
  );
};

export default LandingPages;
